/**
 * Motion-powered animations for the burger menus, search modal, filters
 * dialog, cart drawer, and the currency/country switcher. Uses Motion
 * (motion.dev), vendored at assets/motion.js - see that file's header
 * comment for version/update instructions.
 *
 * The desktop/mobile burger menus, search modal, filters dialog, and cart
 * drawer all already show/hide via a CSS transition keyed off either the
 * details[open] attribute or an .is-open/.active class toggle, which
 * animates smoothly in both directions on its own - that mechanism is
 * left untouched here. This file only adds extra polish once they've
 * opened (staggered nav links/filter groups/cart items, a fade for the
 * search field, a pop for the desktop burger's own panel), triggered by
 * the 'rizon:modal-opened' event dispatched from assets/details-modal.js,
 * assets/rizon-filters-dialog.js and assets/cart-drawer.js.
 *
 * The currency/country switcher panel is different: its visibility is
 * controlled by toggling the native `hidden` attribute (assets/
 * localization-form.js), which forces display:none <-> block. CSS
 * transitions cannot animate across a display:none boundary at all, and
 * CSS @keyframes can only ever animate the reveal half (closing is
 * instant, since `hidden` removes the element from display before any
 * exit animation could run). That's a real limitation, not just a missed
 * detail: getting a smooth close requires deferring the `hidden` attribute
 * until an exit animation finishes, which needs JS either way - so this
 * file exports animatePanelIn/animatePanelOut for localization-form.js to
 * call, with the `hidden` attribute now applied only after the exit
 * animation's promise resolves.
 */
import { animate, inView, stagger } from './motion.js';

// Exposed so classic (non-module) section scripts can reuse the same Motion
// instance without importing the module themselves - e.g. the product
// section's add-to-cart feedback pops the header cart count, and the benefits
// section staggers its columns in on scroll, with these.
window.RizonMotion = { animate, inView, stagger };

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function reduced() {
  return prefersReducedMotion.matches;
}

/* ── Currency/country switcher panel (desktop header + mobile burger) ──
   Scale+fade only (no x/y translation) - Motion's independent transform
   values (x/y/z/scale/rotate) fully own the element's `transform` once
   used, which would silently overwrite this panel's static CSS
   positioning offset (see .rizon-header__localizations/.rizon-mobile-
   menu-modal__localizations .disclosure__list-wrapper margin rules in
   sections/header.liquid - deliberately margin-based, not transform-
   based, for exactly this reason). */
const PANEL_SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.9 };
const PANEL_EXIT = { duration: 0.16, easing: 'ease-in' };

const panelExitControls = new WeakMap();

function animatePanelIn(panel) {
  const running = panelExitControls.get(panel);
  if (running) {
    running.stop();
    panelExitControls.delete(panel);
  }
  if (reduced()) return;
  animate(panel, { opacity: [0, 1], scale: [0.96, 1] }, PANEL_SPRING);
}

function animatePanelOut(panel) {
  if (reduced()) return Promise.resolve();
  const controls = animate(panel, { opacity: [1, 0], scale: [1, 0.96] }, PANEL_EXIT);
  panelExitControls.set(panel, controls);
  return controls.finished.catch(() => {});
}

window.RizonAnimations = { animatePanelIn, animatePanelOut };

/* ── Burger menu / filters dialog / cart drawer / search modal entrance ──
   All of these already show/hide via a CSS transition keyed off either
   details[open] or an .is-open/.active class toggle (never the `hidden`
   attribute), so open+close were already smooth on their own - this only
   adds extra polish once they've opened: dispatched from
   assets/details-modal.js (burger menus, search), assets/
   rizon-filters-dialog.js (filters) and assets/cart-drawer.js (cart), all
   firing the same 'rizon:modal-opened' event so this stays the one place
   that owns "what animates in when a panel like this appears". */
const STAGGER_GROUPS = [
  '.rizon-menu-modal__links > li',
  '.rizon-menu-modal__secondary-links > li',
  '.rizon-mobile-menu-modal__links > li',
  '.rizon-mobile-menu-modal__secondary-links > li',
  '.rizon-filters-dialog__group',
  '.rizon-cart-drawer__item',
];

const SOLO_FADE_TARGETS = ['.rizon-search-modal__field-wrap'];

/* The desktop burger's own panel (.rizon-menu-modal__panel) is a
   full-bleed box with no CSS entrance motion of its own - unlike the
   mobile burger/filters/cart drawers, which visibly slide in as a
   distinct "physical" object (see their translateX CSS transitions),
   the desktop version just appears inside its own already-fading
   backdrop, which reads as barely-there. This gives the panel itself a
   deliberate pop, layered on top of (not replacing) that existing
   backdrop fade. No pre-existing static transform on this element to
   worry about clobbering (unlike the localization panel above). */
const POP_TARGETS = ['.rizon-menu-modal__panel'];

function playModalEntrance(root) {
  if (reduced()) return;

  POP_TARGETS.forEach((selector) => {
    const el = root.querySelector(selector);
    if (!el) return;
    animate(el, { opacity: [0, 1], scale: [0.97, 1] }, PANEL_SPRING);
  });

  STAGGER_GROUPS.forEach((selector) => {
    const items = root.querySelectorAll(selector);
    if (!items.length) return;
    animate(
      items,
      { opacity: [0, 1], y: [10, 0] },
      { duration: 0.35, delay: stagger(0.04), easing: [0.4, 0, 0.2, 1] }
    );
  });

  SOLO_FADE_TARGETS.forEach((selector) => {
    const el = root.querySelector(selector);
    if (!el) return;
    animate(el, { opacity: [0, 1], y: [8, 0] }, { duration: 0.3, delay: 0.05, easing: [0.4, 0, 0.2, 1] });
  });
}

document.addEventListener('rizon:modal-opened', (event) => playModalEntrance(event.target));
